import React, { useState, useEffect } from "react";
import { observer } from "mobx-react";
import { useHistory } from "react-router-dom";
import { agentsData } from "Stores/agentsData";
import AgentSelect from "./AgentSelect";
import { DEFAULT_DAYS, defaultTimezone } from "./DayOfWeekField";
import { SetupPage, When, Step, Field, Row, Schedule, SequenceField, NotifyStep, SkipLine, openInBuilder, clampInt, Need } from "./SetupFlow";

const CreateCompanyProspector = observer(() => {
  const history = useHistory();
  const [name, setName] = useState("");
  const [savedSearchId, setSavedSearchId] = useState("");
  const [titles, setTitles] = useState("");
  const [perCompany, setPerCompany] = useState(5);
  const [sequenceId, setSequenceId] = useState("");
  const [sequenceName, setSequenceName] = useState("");
  const [runWhen, setRunWhen] = useState("DAILY");
  const [time, setTime] = useState("08:00");
  const [days, setDays] = useState(DEFAULT_DAYS);
  const [dailyCap, setDailyCap] = useState(25);
  const [notifyMode, setNotifyMode] = useState("DAILY_DIGEST");
  const [saving, setSaving] = useState(false);
  const timezone = defaultTimezone();

  useEffect(() => {
    agentsData.fetchSavedSearches();
  }, []);

  const canSubmit = !!savedSearchId && !!sequenceId;

  const buildPayload = () => ({
    name: name.trim() || "Company prospector",
    savedSearchId: savedSearchId || undefined,
    titles: titles.trim() || undefined,
    perCompany: clampInt(perCompany, 1, 25, 5),
    sequenceId,
    dailyCap: clampInt(dailyCap, 1, null, 25),
    notifyMode,
    timezone,
    ...(runWhen === "MANUAL" ? {} : { frequency: runWhen, time, ...(runWhen === "WEEKLY" ? { days } : {}) })
  });

  const submit = async () => {
    if (!canSubmit) return;
    setSaving(true);
    const agent = await agentsData.createCompanyProspector(buildPayload());
    setSaving(false);
    if (agent && agent.id) history.push(`/agents/${agent.id}`);
  };

  const customize = () => openInBuilder(history, "company-prospector", buildPayload(), setSaving);

  return (
    <SetupPage
      title="Hire · Companies → people"
      name={{ value: name, onChange: setName, placeholder: "Company prospector" }}
      bar={{ canSubmit, saving, onCreate: submit, onCustomize: customize, onCancel: () => history.push("/agents"), reason: "Pick a saved search and a sequence to create it." }}>
      <When icon="clock">
        <Schedule runWhen={runWhen} setRunWhen={setRunWhen} days={days} setDays={setDays} time={time} setTime={setTime} timezone={timezone} />
      </When>
      <Step icon="building" tone="violet" title="Find companies">
        <Field hint="Uses the company filters of a search saved in List Builder's Companies tab.">
          <Need ok={!!savedSearchId}><AgentSelect searchable placeholder="Pick a saved search" value={savedSearchId}
            onChange={setSavedSearchId}
            options={(agentsData.savedSearches || []).map(ss => ({ value: ss.id, label: ss.label }))} /></Need>
        </Field>
      </Step>
      <Step icon="user" tone="violet" title="Find people there">
        <Row>
          <Field label="Job titles">
            <input className="ag-input" value={titles} placeholder="Anyone, e.g. VP Sales" onChange={e => setTitles(e.target.value)} />
          </Field>
          <Field label="People per company">
            <input className="ag-input" type="number" min="1" max="25" value={perCompany} onChange={e => setPerCompany(e.target.value)} />
          </Field>
          <Field label="Contacts per run">
            <input className="ag-input" type="number" min="1" value={dailyCap} onChange={e => setDailyCap(e.target.value)} />
          </Field>
        </Row>
      </Step>
      <Step icon="mail" tone="violet" title="Find their emails" tags={[{ label: "For email sequences" }]} />
      <Step icon="send" tone="amber" title="Add to a sequence">
        <SequenceField id={sequenceId} name={sequenceName} onPick={s => { setSequenceId(s.id); setSequenceName(s.name); }} />
      </Step>
      <NotifyStep value={notifyMode} onChange={setNotifyMode} />
      <SkipLine />
    </SetupPage>
  );
});

export default CreateCompanyProspector;
